import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/format';
import { client_work_path, pricing_path, signup_path, templates_path } from '@/rails/routes';
import { Link } from '@inertiajs/react';
import type { ReactNode } from 'react';

const CONTACT_EMAIL = 'lance.foley@caprocklabs.com';

const SECTION = 'border-t py-14 sm:py-20';
const CONTAINER = 'mx-auto max-w-6xl px-4 sm:px-6';
const FOCUS_RING =
  'rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring';
const TEXT_LINK = `underline decoration-accent underline-offset-4 hover:decoration-foreground ${FOCUS_RING}`;

interface PlanPrice {
  key: string;
  name: string;
  monthlyPriceCents: number;
}

interface TemplateCard {
  key: string;
  name: string;
  summary: string;
}

interface Props {
  signupOpen: boolean;
  plans: PlanPrice[];
  templates: TemplateCard[];
}

interface SpecRow {
  label: string;
  value: string;
}

const SPEC_SHEET: SpecRow[] = [
  { label: 'Based in', value: 'Litchfield, Minnesota' },
  { label: 'Building operations software since', value: '2021' },
  { label: 'Payments go to', value: 'Your own Stripe or Square account' },
  { label: 'Our cut of your payments', value: 'None' },
];

interface Inherited {
  title: string;
  body: string;
}

// Written the way an owner would say it. No tables, buckets, mail servers, or keys.
const INHERITED: Inherited[] = [
  {
    title: 'Customers, jobs, and invoices',
    body: 'Lists for the things you keep track of. Add one, look one up, and change it when the work changes.',
  },
  {
    title: 'Photos and attachments',
    body: 'Put the site photo, the signed form, or the PDF on the record it belongs to.',
  },
  {
    title: 'Email the receipt',
    body: 'Send an invoice or a receipt to your customer from the record it is about.',
  },
  {
    title: 'Take a payment',
    body: 'Press Connect and sign in to your own Stripe or Square account. Your customer pays from a link on the invoice. The money goes to you. CAP Rock Labs takes no cut.',
  },
  {
    title: 'Send this to QuickBooks',
    body: 'Press Connect once. After that, a button on the invoice sends it to your books.',
  },
];

interface EyebrowProps {
  children: ReactNode;
}

// The bronze is the rule only. At this size bronze text on paper is too faint to read.
const Eyebrow = ({ children }: EyebrowProps) => (
  <div className="flex items-center gap-3">
    <span className="bg-accent h-px w-6 shrink-0" aria-hidden="true" />
    <span className="text-muted-foreground text-xs font-medium tracking-[0.18em] uppercase">
      {children}
    </span>
  </div>
);

const Show = ({ signupOpen, plans, templates }: Props) => {
  // The signup page explains what "Request access" means while signup is closed.
  const signupLabel = signupOpen ? 'Get started' : 'Request access';

  return (
    <div className="bg-background text-foreground">
      <section className={`${CONTAINER} py-14 sm:py-24`}>
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="flex flex-col gap-6 lg:col-span-7">
            <Eyebrow>Apps for running a business</Eyebrow>
            <h1 className="authority-display text-4xl leading-[1.08] text-balance sm:text-6xl">
              Describe the app you run your business on.
            </h1>
            <p className="text-muted-foreground max-w-xl text-lg leading-relaxed">
              Say what you keep track of and CAP Rock Labs builds the app. You get your lists, your
              photos and files, email to your customers, a way to take payment, and a button that
              sends it to QuickBooks.
            </p>
            <p className="max-w-xl leading-relaxed">There is nothing technical to set up.</p>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
              <Button asChild size="lg">
                <Link href={signup_path()}>{signupLabel}</Link>
              </Button>
              <Link href={templates_path()} className={`text-sm font-medium ${TEXT_LINK}`}>
                See the templates
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <Eyebrow>At a glance</Eyebrow>
            <dl className="mt-5 border-t">
              {SPEC_SHEET.map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 border-b py-3 text-sm"
                >
                  <dt className="text-muted-foreground">{row.label}</dt>
                  <dd className="text-right font-medium">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className={`${SECTION} bg-card`}>
        <div className={CONTAINER}>
          <div className="flex max-w-2xl flex-col gap-4">
            <Eyebrow>What every app inherits</Eyebrow>
            <h2 className="text-3xl tracking-tight sm:text-4xl">
              The parts every business app needs are already there.
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              You describe the work. These come with every app.
            </p>
          </div>

          <dl className="mt-10 border-t">
            {INHERITED.map((item) => (
              <div
                key={item.title}
                className="grid gap-2 border-b py-5 sm:grid-cols-[16rem_1fr] sm:items-baseline sm:gap-10"
              >
                <dt className="font-medium">{item.title}</dt>
                <dd className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
                  {item.body}
                </dd>
              </div>
            ))}
          </dl>

          <p className="text-muted-foreground mt-6 text-sm leading-relaxed">
            Which plan turns on live payments and QuickBooks is on the{' '}
            <Link href={pricing_path()} className={`text-foreground ${TEXT_LINK}`}>
              pricing page
            </Link>
            .
          </p>
        </div>
      </section>

      <section className={SECTION}>
        <div className={CONTAINER}>
          <div className="flex max-w-2xl flex-col gap-4">
            <Eyebrow>Templates</Eyebrow>
            <h2 className="text-3xl tracking-tight sm:text-4xl">
              Start from an app that is already laid out.
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Each template comes with its lists and fields in place. Change them to match how you
              work.
            </p>
          </div>

          {templates.length > 0 ? (
            <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {templates.map((template) => (
                <li key={template.key} className="flex">
                  <Link
                    href={templates_path({ anchor: template.key })}
                    className="bg-card hover:border-foreground focus-visible:outline-ring flex w-full flex-col gap-2 border p-5 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
                  >
                    <span className="font-medium">{template.name}</span>
                    <span className="text-muted-foreground text-sm leading-relaxed">
                      {template.summary}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}

          <p className="mt-8 text-sm">
            <Link href={templates_path()} className={`font-medium ${TEXT_LINK}`}>
              See what is in each template
            </Link>
          </p>
        </div>
      </section>

      <section className={SECTION}>
        <div className={CONTAINER}>
          <div className="flex max-w-2xl flex-col gap-4">
            <Eyebrow>Plans</Eyebrow>
            <h2 className="text-3xl tracking-tight sm:text-4xl">
              Pick the plan that fits how much you run on it.
            </h2>
          </div>

          {plans.length > 0 ? (
            <dl className="mt-10 grid border-t sm:grid-cols-3 sm:border-t-0 sm:border-l">
              {plans.map((plan) => (
                <div
                  key={plan.key}
                  className="flex items-baseline justify-between gap-6 border-b py-4 sm:flex-col sm:justify-start sm:gap-2 sm:border-y sm:border-r sm:px-6 sm:py-6"
                >
                  <dt className="font-medium">{plan.name}</dt>
                  <dd className="text-muted-foreground tabular-nums">
                    {plan.monthlyPriceCents > 0 ? (
                      <>
                        <span className="text-foreground text-2xl font-medium">
                          {formatPrice(plan.monthlyPriceCents)}
                        </span>{' '}
                        a month
                      </>
                    ) : (
                      <span className="text-foreground text-2xl font-medium">Free</span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}

          <p className="mt-8 text-sm">
            <Link href={pricing_path()} className={`font-medium ${TEXT_LINK}`}>
              Compare the plans
            </Link>
          </p>
        </div>
      </section>

      <section className={SECTION}>
        <div className={`${CONTAINER} grid gap-8 lg:grid-cols-12 lg:gap-16`}>
          <div className="flex flex-col gap-4 lg:col-span-7">
            <Eyebrow>Client work</Eyebrow>
            <h2 className="text-3xl tracking-tight sm:text-4xl">
              CAP Rock Labs also builds custom software for clients.
            </h2>
          </div>
          <div className="flex flex-col gap-4 lg:col-span-5">
            <p className="text-muted-foreground leading-relaxed">
              Some operations need a system built around them. We build those for companies in oil
              and gas, construction, real estate, and local business. That work started in 2021 and
              it continues.
            </p>
            <p className="text-sm">
              <Link href={client_work_path()} className={`font-medium ${TEXT_LINK}`}>
                See the client work
              </Link>
            </p>
          </div>
        </div>
      </section>

      <section className={SECTION}>
        <div className={`${CONTAINER} grid gap-8 lg:grid-cols-2 lg:items-end lg:gap-16`}>
          <div className="flex flex-col gap-6">
            <Eyebrow>Contact</Eyebrow>
            <h2 className="max-w-lg text-3xl leading-tight tracking-tight sm:text-4xl">
              Start with a template, or tell us what you need.
            </h2>
            <div>
              <Button asChild size="lg">
                <Link href={signup_path()}>{signupLabel}</Link>
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t pt-6 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-10">
            <p className="text-muted-foreground text-sm">Have a question first? Email us.</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className={`w-fit text-lg font-medium break-all sm:text-xl ${TEXT_LINK}`}
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Show;
